<script lang="ts" setup>
	import { computed, watch } from 'vue'
	import { Collapse } from 'vue-collapsed'
	import { useToast } from 'vue-toastification'

	import PayProcessorIcon from '@/components/icons/PayProcessorIcon.vue'

	import { useAuth } from '@/composables/useAuth'
	import useFixedNumber from '@/composables/useFixedNumber'

	import { Company } from '@/modules/companies/interfaces/company'
	import useUserGuaranteeCards from '@/modules/users/composables/useUserGuaranteeCards'

	import OpenPayForm from './OpenPayForm.vue'
	import CheckIcon from './ui/CheckIcon.vue'
	import SelectableCard from './ui/SelectableCard.vue'

	interface Card {
		id: string
		name: string
		alias?: string
		institution: string
		holder_name: string
		ownership?: 'user' | 'company'
	}

	interface Credit {
		availablePercentage?: number
	}

	interface Props {
		paymentOptions: {
			name: string
			value: string
			icon: string
		}[]
		selectedPaymentMethod?: string
		companyCards?: Card[]
		userCards?: Card[]
		travelerCards?: Card[]
		credit?: Credit | null
		company: Company
		isInternational?: boolean
		hotelCountryCode?: string
		paymentMethod?: string
		memberId?: number
		travelerEmail?: string
		travelerId?: number
		travelerUserId?: number
	}

	const props = withDefaults(defineProps<Props>(), {
		selectedPaymentMethod: '',
		companyCards: () => [],
		userCards: () => [],
		travelerCards: () => [],
		credit: null,
	})

	const selectedCardId = defineModel<string>('selectedCardId')

	const emit = defineEmits<{
		'update:selectedPaymentMethod': [value: string]
	}>()

	const { user } = useAuth()

	const toast = useToast()

	const checkPercentage = (percentage: number) => {
		if (percentage >= 70 && percentage <= 100) {
			return 'bg-green-600'
		} else if (percentage >= 30 && percentage <= 69) {
			return 'bg-yellow-600'
		} else if (percentage >= 0 && percentage <= 29) {
			return 'bg-red-600'
		}
	}

	const isSelectedPaymentMethod = (optionValue: string) => {
		return props.selectedPaymentMethod === optionValue
	}

	const ownershipLabel = (ownership?: 'user' | 'company') => {
		if (ownership === 'company') return 'Corporativa'
		if (ownership === 'user') return 'Personal'
		return ''
	}

	const ownershipBadgeClass = (ownership?: 'user' | 'company') => {
		return ownership === 'company' ?
				'bg-emerald-50 text-emerald-700 border-emerald-200'
			:	'bg-blue-50 text-blue-700 border-blue-200'
	}

	const guaranteeCardUserId = computed(() => props.memberId || user.value!.id)

	const { userGuaranteeCards, deleteCard, refetch } = useUserGuaranteeCards({
		params: { userId: guaranteeCardUserId.value },
		immediate: true,
	})

	const deactivateCard = async (cardId: string) => {
		try {
			await deleteCard({
				userId: guaranteeCardUserId.value,
				cardId,
			})
			await refetch()
			toast.success('Tarjeta de garantía eliminada correctamente.')
		} catch (error) {
			toast.error(
				'Ocurrió un error al eliminar la tarjeta de garantía. Por favor, intente nuevamente.'
			)
		}
	}

	// Inicializar selectedCardId con la tarjeta por defecto cuando se cargan las tarjetas de garantía
	watch(
		userGuaranteeCards,
		(cards) => {
			if (cards && cards.length > 0 && !selectedCardId.value) {
				const defaultCard = cards.find((card) => card.isDefault)
				if (defaultCard) {
					selectedCardId.value = defaultCard.id
				}
			}
		},
		{ immediate: true }
	)
</script>

<template>
	<!-- Credit info -->
	<Collapse
		:when="isSelectedPaymentMethod('credit')"
		class="v-collapse"
	>
		<div class="bg-white rounded-lg p-3 lg:p-4 border border-gray-200">
			<div v-if="credit && company">
				<div class="flex items-start gap-4">
					<div
						class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-200 bg-white p-1"
						v-if="company"
					>
						<img
							v-if="company"
							:src="company.isotipo || company.logo"
							:alt="company.name"
							class="max-w-full max-h-full object-contain"
						/>
					</div>

					<div class="flex-1">
						<h3 class="font-inter font-semibold text-blue-900 mb-2">
							{{
								!user?.internal ?
									'Su empresa se encargará de los costos'
								:	`La empresa ${props.company.name} responderá por los costos`
							}}
						</h3>
						<p class="text-sm text-blue-700 mb-4">Crédito disponible</p>
						<div class="bg-white rounded-full h-3 overflow-hidden">
							<div
								:style="{
									width: `${credit?.availablePercentage}%`,
								}"
								:class="[
									'h-full transition-all duration-500 flex items-center justify-end pr-2',
									checkPercentage(credit!.availablePercentage!!),
								]"
							>
								<span class="text-xs font-inter font-bold text-white">
									{{
										useFixedNumber(credit.availablePercentage || 0).fixedNumber
									}}%
								</span>
							</div>
						</div>
					</div>
				</div>

				<!-- Low credit warning -->
				<div
					v-if="credit!.availablePercentage!! <= 29"
					class="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4"
				>
					<div class="flex gap-3">
						<svg
							class="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
							></path>
						</svg>
						<div>
							<h4 class="font-inter font-semibold text-amber-800">
								¡Atención! Crédito bajo
							</h4>
							<p class="text-sm text-amber-700 mt-1">
								{{
									!user?.internal ?
										'Su crédito disponible está próximo a agotarse. Le recomendamos contactar a su área administrativa.'
									:	`El crédito de ${user.companyName} está por agotarse. Le recomendamos avisar al socio responsable.`
								}}
							</p>
						</div>
					</div>
				</div>
			</div>

			<div
				v-else
				class="animate-pulse"
			>
				<div class="flex items-start gap-4">
					<div class="w-10 h-10 bg-blue-200 rounded-lg flex-shrink-0"></div>

					<div class="flex-1 space-y-3">
						<div class="h-5 bg-blue-200 rounded-md w-3/4"></div>

						<div class="h-4 bg-blue-200 rounded-md w-1/3"></div>

						<div class="bg-white rounded-full h-3 overflow-hidden">
							<div
								class="h-full bg-gradient-to-r from-blue-200 via-blue-100 to-blue-200 bg-[length:200%_100%] animate-shimmer"
								style="width: 60%"
							></div>
						</div>
					</div>
				</div>

				<!-- Info text skeleton -->
				<div class="mt-4 space-y-2">
					<div class="h-3 bg-blue-200 rounded w-full"></div>
					<div class="h-3 bg-blue-200 rounded w-5/6"></div>
				</div>
			</div>
		</div>
	</Collapse>

	<!-- Company cards -->
	<Collapse
		:when="isSelectedPaymentMethod('companyCard')"
		class="v-collapse"
	>
		<div class="space-y-4">
			<!-- Subtítulo OpenPay -->
			<div class="border-l-4 border-blue-400 bg-blue-50 p-3 rounded-r-lg">
				<div class="flex items-start gap-2">
					<svg
						class="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
						/>
					</svg>
					<div>
						<h4 class="font-inter font-semibold text-blue-800 text-sm">
							Tarjetas de Empresa - Pago con OpenPay
						</h4>
						<p class="text-xs text-blue-700 mt-1">
							El cargo se realizará de forma inmediata y segura a través de la
							plataforma OpenPay.
						</p>
					</div>
				</div>
			</div>

			<template v-if="companyCards.length === 0">
				<p class="text-xs lg:text-sm text-gray-600">
					No hay tarjetas disponibles.
				</p>
				<div
					class="border-2 border-dashed border-gray-300 rounded-lg p-3 lg:p-4 text-center"
				>
					<div class="flex flex-col items-center gap-3">
						<svg
							class="w-8 h-8 lg:w-12 lg:h-12 text-gray-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M3 5h18a2 2 0 012 2v2H1V7a2 2 0 012-2zm-2 6h22v6a2 2 0 01-2 2H3a2 2 0 01-2-2v-6zm3 4h4m6-10L3 19"
							/>
						</svg>
						<p class="text-xs lg:text-sm text-gray-500">
							Por favor, contacte a su administrador para agregar tarjetas de
							empresa.
						</p>
					</div>
				</div>
			</template>

			<template v-else>
				<p
					class="font-inter font-medium text-sm lg:text-base text-gray-700 mb-4"
				>
					Seleccione una tarjeta:
					<span class="text-red-500">*</span>
				</p>

				<SelectableCard
					v-for="card in companyCards"
					:key="card.id"
					:selected="selectedCardId === card.id"
					:show-check-icon="false"
					@select="selectedCardId = card.id"
				>
					<div class="flex items-center justify-between">
						<div class="flex items-center gap-3 min-w-0 flex-1">
							<PayProcessorIcon
								:processor="card.institution"
								class-name="w-10 h-6 lg:w-12 lg:h-8 rounded flex-shrink-0"
							/>
							<div class="min-w-0 flex-1">
								<div class="flex items-center gap-2 flex-wrap">
									<p
										class="font-inter font-medium text-sm lg:text-base text-gray-900 truncate"
									>
										{{ card.name }}
									</p>
									<span
										v-if="card.ownership"
										class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border flex-shrink-0"
										:class="ownershipBadgeClass(card.ownership)"
									>
										{{ ownershipLabel(card.ownership) }}
									</span>
								</div>
								<p
									v-if="card.alias"
									class="text-xs lg:text-sm text-gray-600 truncate"
								>
									({{ card.alias }})
								</p>
								<p class="text-xs lg:text-sm text-gray-700 capitalize truncate">
									{{ card.holder_name }}
								</p>
							</div>
						</div>
						<CheckIcon
							v-if="selectedCardId === card.id"
							size="md"
							color="blue"
						/>
					</div>
				</SelectableCard>
			</template>
		</div>
	</Collapse>

	<!-- User cards -->
	<Collapse
		:when="isSelectedPaymentMethod('userCards')"
		class="v-collapse"
	>
		<div class="space-y-4">
			<!-- Subtítulo OpenPay -->
			<div class="border-l-4 border-blue-400 bg-blue-50 p-3 rounded-r-lg">
				<div class="flex items-start gap-2">
					<svg
						class="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
						/>
					</svg>
					<div>
						<h4 class="font-inter font-semibold text-blue-800 text-sm">
							Mis Tarjetas - Pago con OpenPay
						</h4>
						<p class="text-xs text-blue-700 mt-1">
							El cargo se realizará de forma inmediata y segura a través de la
							plataforma OpenPay.
						</p>
					</div>
				</div>
			</div>

			<template v-if="userCards.length === 0">
				<template v-if="travelerEmail">
					<p class="text-xs lg:text-sm text-gray-600 mb-3">
						No hay tarjetas guardadas. Agrega una para continuar.
					</p>
					<OpenPayForm
						:hotel-country-code="hotelCountryCode"
						:is-international="isInternational"
						:member-id="memberId"
						:traveler-id="travelerId"
						@added="(e) => emit('update:selectedPaymentMethod', e)"
					/>
				</template>
				<template v-else>
					<p class="text-xs lg:text-sm text-gray-600">
						No hay tarjetas disponibles. El viajero no tiene correo registrado para agregar tarjetas.
					</p>
				</template>
			</template>

			<template v-else>
				<p
					class="font-inter font-medium text-sm lg:text-base text-gray-700 mb-4"
				>
					Seleccione una tarjeta:
					<span class="text-red-500">*</span>
				</p>

				<SelectableCard
					v-for="card in userCards"
					:key="card.id"
					:selected="selectedCardId === card.id"
					:show-check-icon="false"
					@select="selectedCardId = card.id"
				>
					<div class="flex items-center justify-between">
						<div class="flex items-center gap-3 min-w-0 flex-1">
							<PayProcessorIcon
								:processor="card.institution"
								class-name="w-10 h-6 lg:w-12 lg:h-8 rounded flex-shrink-0"
							/>
							<div class="min-w-0 flex-1">
								<div class="flex items-center gap-2 flex-wrap">
									<p
										class="font-inter font-medium text-sm lg:text-base text-gray-900 truncate"
									>
										{{ card.name }}
									</p>
									<span
										v-if="card.ownership"
										class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border flex-shrink-0"
										:class="ownershipBadgeClass(card.ownership)"
									>
										{{ ownershipLabel(card.ownership) }}
									</span>
								</div>
								<p
									v-if="card.alias"
									class="text-xs lg:text-sm text-gray-600 truncate"
								>
									({{ card.alias }})
								</p>
								<p class="text-xs lg:text-sm text-gray-700 capitalize truncate">
									{{ card.holder_name }}
								</p>
							</div>
						</div>
						<CheckIcon
							v-if="selectedCardId === card.id"
							size="md"
							color="blue"
						/>
					</div>
				</SelectableCard>
			</template>
		</div>
	</Collapse>

	<!-- Guarantee Cards -->
	<Collapse
		:when="isSelectedPaymentMethod('destination')"
		class="v-collapse"
	>
		<div class="space-y-4">
			<template v-if="userGuaranteeCards?.length === 0">
				<p class="text-xs lg:text-sm text-gray-600">
					No hay tarjetas de garantía disponibles.
				</p>
			</template>

			<template v-else>
				<p
					class="font-inter font-medium text-sm lg:text-base text-gray-700 mb-4"
				>
					Seleccione una tarjeta de garantía:
					<span class="text-red-500">*</span>
				</p>

				<SelectableCard
					v-for="card in userGuaranteeCards"
					:key="card.id"
					:selected="
						selectedCardId === card.id || (!selectedCardId && card.isDefault)
					"
					:show-check-icon="false"
					@select="
						() => {
							selectedCardId = card.id
						}
					"
				>
					<div class="flex items-center justify-between">
						<div class="flex items-center gap-3 min-w-0 flex-1">
							<PayProcessorIcon
								:processor="card.brand"
								class-name="w-10 h-6 lg:w-12 lg:h-8 rounded flex-shrink-0"
							/>
							<div class="min-w-0 flex-1">
								<div class="flex items-center gap-2 flex-wrap">
									<p
										class="font-inter font-medium text-sm lg:text-base text-gray-900 truncate"
									>
										{{ card.alias || card.maskedNumber }}
									</p>
									<span
										v-if="card.isDefault"
										class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
									>
										Por defecto
									</span>
									<span
										v-if="card.isExpired"
										@click="deactivateCard(card.id)"
										class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
									>
										Expirada
									</span>
								</div>
								<p
									v-if="card.alias"
									class="text-xs lg:text-sm text-gray-600 truncate"
								>
									{{ card.maskedNumber }}
								</p>
								<p class="text-xs lg:text-sm text-gray-700 capitalize truncate">
									{{ card.holderName }}
								</p>
							</div>
						</div>
						<CheckIcon
							v-if="
								selectedCardId === card.id ||
								(!selectedCardId && card.isDefault)
							"
							size="md"
							color="blue"
						/>
					</div>
				</SelectableCard>
			</template>
		</div>
	</Collapse>

	<!-- Traveler cards -->
	<Collapse
		:when="isSelectedPaymentMethod('travelerCards')"
		class="v-collapse"
	>
		<div class="space-y-4">
			<div class="border-l-4 border-indigo-400 bg-indigo-50 p-3 rounded-r-lg">
				<div class="flex items-start gap-2">
					<span class="material-symbols-outlined text-indigo-500 text-[18px] flex-shrink-0 mt-0.5">person_pin</span>
					<div>
						<h4 class="font-inter font-semibold text-indigo-800 text-sm">
							Tarjetas del Viajero - Pago con OpenPay
						</h4>
						<p class="text-xs text-indigo-700 mt-1">
							El cargo se realizará a la tarjeta del viajero de forma inmediata y segura.
						</p>
					</div>
				</div>
			</div>

			<template v-if="travelerCards.length === 0">
				<template v-if="travelerEmail">
					<p class="text-xs lg:text-sm text-gray-600 mb-3">
						El viajero no tiene tarjetas guardadas. Agrega una para continuar.
					</p>
					<OpenPayForm
						:hotel-country-code="hotelCountryCode"
						:is-international="isInternational"
						:member-id="memberId"
						:traveler-id="travelerUserId"
						:added-target="'travelerCards'"
						@added="(e) => emit('update:selectedPaymentMethod', e)"
					/>
				</template>
				<template v-else>
					<p class="text-xs lg:text-sm text-gray-600">
						No hay tarjetas disponibles. El viajero no tiene correo registrado para agregar tarjetas.
					</p>
				</template>
			</template>

			<template v-else>
				<p class="font-inter font-medium text-sm lg:text-base text-gray-700 mb-4">
					Seleccione una tarjeta del viajero:
					<span class="text-red-500">*</span>
				</p>
				<SelectableCard
					v-for="card in travelerCards"
					:key="card.id"
					:selected="selectedCardId === card.id"
					:show-check-icon="false"
					@select="selectedCardId = card.id"
				>
					<div class="flex items-center justify-between">
						<div class="flex items-center gap-3 min-w-0 flex-1">
							<PayProcessorIcon
								:processor="card.institution"
								class-name="w-10 h-6 lg:w-12 lg:h-8 rounded flex-shrink-0"
							/>
							<div class="min-w-0 flex-1">
								<div class="flex items-center gap-2 flex-wrap">
									<p class="font-inter font-medium text-sm lg:text-base text-gray-900 truncate">
										{{ card.name }}
									</p>
									<span
										v-if="card.ownership"
										class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border flex-shrink-0"
										:class="ownershipBadgeClass(card.ownership)"
									>
										{{ ownershipLabel(card.ownership) }}
									</span>
								</div>
								<p v-if="card.alias" class="text-xs lg:text-sm text-gray-600 truncate">
									({{ card.alias }})
								</p>
								<p class="text-xs lg:text-sm text-gray-700 capitalize truncate">
									{{ card.holder_name }}
								</p>
							</div>
						</div>
						<CheckIcon v-if="selectedCardId === card.id" size="md" color="blue" />
					</div>
				</SelectableCard>
			</template>
		</div>
	</Collapse>

	<!-- Add new card -->
	<Collapse
		:when="isSelectedPaymentMethod('addAndPay')"
		class="v-collapse"
	>
		<div class="bg-gray-50 rounded-lg p-4 lg:p-6">
			<h3
				class="font-inter font-semibold text-base lg:text-lg text-gray-900 mb-4"
			>
				Introduzca los datos de su tarjeta:
				<span class="text-red-500">*</span>
			</h3>
			<OpenPayForm
				:hotel-country-code="hotelCountryCode"
				:is-international="isInternational"
				:member-id="memberId"
				:traveler-id="travelerId"
				@added="(e) => emit('update:selectedPaymentMethod', e)"
			/>
		</div>
	</Collapse>
</template>

<style scoped>
	@keyframes shimmer {
		0% {
			background-position: 200% 0;
		}
		100% {
			background-position: -200% 0;
		}
	}

	.animate-shimmer {
		animation: shimmer 1.5s ease-in-out infinite;
	}
</style>
